import { Close } from "@components/icons"
import ImageHelper from "@components/ImageHelper"
import animation, { cubicBezierBackgroundColor } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import _ from "lodash-es"
import React from "react"
import Modal from "react-modal"
import styled, { createGlobalStyle } from "styled-components"

import { YotpoReview } from "../../../reviews/types"
import Review from "./Review"
import Slider from "./Slider"

export const Container = styled.div`
  will-change: transform;
  width: 100vw;
  min-height: 100vh;
  padding: 0;
  background-color: ${colors.white};
  pointer-events: initial;

  @media ${breakpoints.laptop} {
    transform: translateY(56px);
    transition: transform ${animation.medium} ${cubicBezierBackgroundColor};
    top: 0;
    bottom: 0;
    width: calc(7 / 12 * 100vw);
    height: calc((7 / 12 * 100vw) / 1.16 + 248px);
    margin: auto;
    min-height: calc((7 / 12 * 100vw) / 1.16 + 248px);
    background-color: transparent;
  }
`

export const Content = styled.div`
  position: relative;
  display: block;
  background-color: ${colors.white};

  @media ${breakpoints.laptop} {
    display: flex;
  }
`

export const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;

  @media ${breakpoints.laptop} {
    top: 16px;
    right: 16px;
  }
`

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  padding: 2px;
  border: none;
  border-radius: 2px;
  background: ${colors.dividerLineGray};
  cursor: pointer;

  &:focus {
    outline: solid ${colors.mediumDarkGray} thin;
  }

  @media ${breakpoints.laptop} {
    width: 56px;
    height: 56px;
    padding: 8px;
  }
`

const ImageWrapper = styled.div`
  flex-shrink: 0;
  width: 100%;
  padding: 48px;
  background-color: ${colors.black};

  img {
    top: 0px;
    bottom: 0px;
    left: 0px;
    right: 0;
    margin: auto;
    object-fit: contain;
    object-position: center center;
  }

  @media ${breakpoints.laptop} {
    width: calc(4.5 / 12 * 100vw);
    padding: 0;

    img {
      top: 0px;
      bottom: 0px;
      left: 0px;
      right: 0;
      margin: auto;
      width: 90%;
      height: 90%;
      object-fit: contain;
      object-position: center center;
    }
  }
`

const ReviewWrapper = styled.div`
  flex-shrink: 0;
  width: 100%;
  padding: 32px 20px;

  @media ${breakpoints.laptop} {
    width: calc(2.5 / 12 * 100vw);
    padding: 80px 32px 0;
  }
`

const SliderWrapper = styled.div`
  height: 232px;
`

const TransparentDivider = styled.div`
  display: none;
  height: 16px;
  background: transparent;

  @media ${breakpoints.laptop} {
    display: initial;
  }
`

const ModalAdapter = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  bodyOpenClassName,
  className,
  modalClassName,
  ...props
}: any) => {
  return (
    // @ts-expect-error Ignore
    <Modal className={modalClassName} portalClassName={className} {...props} />
  )
}

const StyledModal = styled(ModalAdapter).attrs({
  modalClassName: {
    afterOpen: "ReactModal__Content--after-open",
    base: "ReactModal__Content",
    beforeClose: "ReactModal__Content--before-close",
  },
  overlayClassName: {
    afterOpen: "ReactModal__Overlay--after-open",
    base: "ReactModal__Overlay",
    beforeClose: "ReactModal__Overlay--before-close",
  },
})`
  .ReactModal__Content {
    z-index: 1001;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    outline: none;
    pointer-events: none;

    @media ${breakpoints.laptop} {
      &--after-open {
        ${Container} {
          transform: translateY(0);
        }
      }

      &--before-close {
        ${Container} {
          transform: translateY(56px);
        }
      }
    }
  }

  .ReactModal__Overlay {
    z-index: 1001;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 1;
    transform: translateX(100%);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    will-change: opacity, transform;
    transition:
      opacity ${animation.quick} ${cubicBezierBackgroundColor},
      transform ${animation.medium} ${cubicBezierBackgroundColor};
    background: none;

    @media ${breakpoints.laptop} {
      opacity: 0;
      transform: translateX(0);
      background: rgba(0, 0, 0, 0.5);
    }

    &--after-open {
      opacity: 1;
      transform: translateX(0);
    }

    &--before-close {
      opacity: 1;
      transform: translateX(100%);

      @media ${breakpoints.laptop} {
        opacity: 0;
        transform: translateX(0);
      }
    }
  }
`

const GlobalStyle = createGlobalStyle`
  .ReactModal__Body--open {
    overflow: hidden;
  }
  .ReactModal__Html--open {
    overflow: hidden;
  }
` as any

export interface ModalBaseProps extends Modal.Props {}

interface ReviewsImageGalleryModalProps extends ModalBaseProps {
  initialImage: number | undefined
  reviews: YotpoReview[]
}

const contentRef = React.createRef<HTMLDivElement>()

const ReviewsImageGalleryModal = ({
  initialImage,
  reviews,
  ...rest
}: ReviewsImageGalleryModalProps) => {
  React.useEffect(() => {
    if (rest.ariaHideApp !== false) {
      if (document.getElementById("root")) {
        Modal.setAppElement("#root")
      } else {
        Modal.setAppElement("#___gatsby")
      }
    }
  })

  const [currentImage, setCurrentImage] = React.useState<number | undefined>()
  const [currentReview, setCurrentReview] = React.useState<
    undefined | YotpoReview
  >()

  const reviewImages = React.useMemo(() => {
    return _.flatten(
      _.map(reviews, (review) => {
        return review.images_data
          ? _.map(review.images_data, (data) => {
              return {
                alt: review.title,
                id: data.id,
                image: data.original_url,
                onClick: () => {
                  setCurrentImage(data.id)
                },
                review: review.id,
                thumbnail: data.thumb_url,
              }
            })
          : []
      })
    ).filter((image) => !!image)
  }, [reviews])

  const onClickImage = (id) => {
    setCurrentImage(id)
  }

  React.useEffect(() => {
    if (initialImage || (reviewImages && reviewImages[0] && reviewImages[0].id))
      setCurrentImage(initialImage || reviewImages[0].id)
  }, [initialImage])

  React.useEffect(() => {
    const thisReview = reviews.find((review) => {
      if (review.images_data && review.images_data.length > 0) {
        return !!review.images_data.find((image) => {
          return image.id === currentImage
        })
      }
    })

    if (thisReview) {
      setCurrentReview(thisReview)
    }
  }, [currentImage])

  const imageData = reviewImages.find((image) => image.id === currentImage)

  return (
    <StyledModal {...rest} closeTimeoutMS={250}>
      <Container>
        <Content>
          <CloseButtonWrapper>
            <CloseButton onClick={rest.onRequestClose} type="button">
              <Close strokeColor={colors.mediumDarkGray} />
            </CloseButton>
          </CloseButtonWrapper>
          <ImageWrapper>
            {imageData && (
              <ImageHelper alt={imageData.alt || ""} src={imageData.image} />
            )}
          </ImageWrapper>
          <ReviewWrapper>
            {currentReview && (
              <Review onClickImage={onClickImage} review={currentReview} />
            )}
          </ReviewWrapper>
        </Content>
        <TransparentDivider />
        <SliderWrapper>
          <Slider
            contentRef={contentRef}
            currentImage={currentImage}
            images={reviewImages}
          />
        </SliderWrapper>
      </Container>
      <GlobalStyle />
    </StyledModal>
  )
}

export default ReviewsImageGalleryModal
